import { useLocation, useNavigate } from 'react-router';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useFeatureGatingSafe } from '../contexts/FeatureGatingContext';
import { useState, useEffect, useCallback, useMemo, lazy, Suspense, type ReactNode } from 'react';
import { AnimatePresence } from 'motion/react';
import { db, handleFirestoreError, OperationType } from '../lib/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { purgeExpiredTrash } from '../lib/trash';
import { useWorkspaceTabs, syncSuppressAutoTab, shouldSuppressAutoTab } from '../store/workspaceTabsStore';
import { useAppearance } from '../contexts/AppearanceContext';
import WorkspaceTabsBar from './workspace/WorkspaceTabsBar';
import NewTabSearch from './workspace/NewTabSearch';
import ProfileCompleteBanner from './onboarding/ProfileCompleteBanner';
import PlatformAnnouncementBanner from './PlatformAnnouncementBanner';
import InAppNotificationBanner from './InAppNotificationBanner';
import {
  dismissNotificationBanner,
  markNotificationRead,
  pickAutoShowBannerNotification,
  fetchUserNotifications,
} from '../lib/userNotifications';
import type { UserNotification } from '../lib/notificationTypes';
import { formatCaseTitle, formatWorkspaceDraftTabTitle } from '../lib/utils';
import {
  fetchSystemTemplateFresh,
  getDisplayTemplateTitle,
  lexGatSystemTemplateEditPath,
} from '../lib/systemTemplates';
import AppMainNav from './AppMainNav';
import LexCounselGlobalHost from './lexcounsel/LexCounselGlobalHost';
import LexCounselPanel from './lexcounsel/LexCounselPanel';
import { useSystem } from '../contexts/SystemContext';
import { useLexCounselStore } from '../store/lexCounselStore';
import { AppContextMenu } from './context-menu/AppContextMenu';
import {
  buildAppContextMenu,
  getTemplateContextFromTarget,
  shouldShowGlobalContextMenu,
  type AppContextMenuState,
} from './context-menu/buildAppContextMenu';
import { startBlankCaseDraftAndOpenEditor } from '../lib/startBlankCaseDraft';
import { AnimatedOutlet } from './motion/PageTransition';
import { RouteErrorBoundary } from './motion/RouteErrorBoundary';
import { schedulePrefetchCoreLists } from '../lib/listPrefetch';

const CaseDetail = lazy(() => import('../pages/CaseDetail'));
const DraftEditor = lazy(() => import('../pages/DraftEditor'));

function TabContentFallback() {
  return (
    <div className="flex min-h-[40vh] w-full flex-1 items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-slate-800" />
    </div>
  );
}

export default function Layout() {
  const { user, activeWorkspaceId, isAdmin } = useAuth();
  const { showError } = useToast();
  const { check, recordUsage } = useFeatureGatingSafe();
  const location = useLocation();
  const navigate = useNavigate();
  const [platformNotifications, setPlatformNotifications] = useState<UserNotification[]>([]);
  const [bannerNotification, setBannerNotification] = useState<UserNotification | null>(null);

  const { tabs, activeTabId, addTab, setActiveTab, removeTab } = useWorkspaceTabs();
  const [contextMenu, setContextMenu] = useState<AppContextMenuState | null>(null);

  const openWorkspaceSearch = useCallback(() => {
    addTab({ id: '/workspace/new', path: '/workspace/new', title: 'New Tab', type: 'new' });
    navigate('/workspace/new');
  }, [addTab, navigate]);

  // Handle open-workspace-search event
  useEffect(() => {
    const handleOpenSearch = () => {
      openWorkspaceSearch();
    };
    const handleRemoveNewTab = () => removeTab('/workspace/new');
    
    window.addEventListener('open-workspace-search', handleOpenSearch);
    window.addEventListener('remove-new-tab', handleRemoveNewTab);
    
    return () => {
      window.removeEventListener('open-workspace-search', handleOpenSearch);
      window.removeEventListener('remove-new-tab', handleRemoveNewTab);
    };
  }, [removeTab, openWorkspaceSearch]);

  useEffect(() => {
    const handleOpenImport = () => navigate('/uploads?tab=old-cases');
    window.addEventListener('lexgat:open-import-case', handleOpenImport);
    return () => window.removeEventListener('lexgat:open-import-case', handleOpenImport);
  }, [navigate]);

  const userId = user?.uid;

  useEffect(() => {
    if (!activeWorkspaceId || !userId) return;
    return schedulePrefetchCoreLists(activeWorkspaceId);
  }, [activeWorkspaceId, userId]);

  // Update tabs when location changes to a tabbable route
  useEffect(() => {
    const path = location.pathname;
    syncSuppressAutoTab(path);

    const isCases = path.startsWith('/cases/') && path !== '/cases';
    const isDrafts = path.startsWith('/drafts/') && path !== '/drafts';
    const isNew = path === '/workspace/new';

    if (isCases || isDrafts || isNew) {
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }

      const state = useWorkspaceTabs.getState();
      if (!isNew && state.activeTabId === '/workspace/new') {
        removeTab('/workspace/new');
      }

      // Ensure a draft tab exists before suppress check (avoids "Loading workspace…" after create)
      if (isDrafts) {
        const parts = path.split('/');
        if (parts[2] && parts[2] !== 'system' && !state.tabs.some((t) => t.id === path)) {
          const entityId = parts[2];
          addTab({ id: path, path, title: 'Loading...', type: 'draft', entityId });
        }
      }

      if (!shouldSuppressAutoTab(path)) {
        setActiveTab(path);
      }
    }

    if (shouldSuppressAutoTab(path)) {
      return;
    }

    if (isCases) {
      const caseId = path.split('/')[2];
      const tabState = useWorkspaceTabs.getState();

      if (!tabState.tabs.some((t) => t.id === path)) {
        addTab({ id: path, path, title: 'Loading...', type: 'case', entityId: caseId });
      } else if (!shouldSuppressAutoTab(path)) {
        setActiveTab(path);
      }

      // Update title async
      getDoc(doc(db, 'workspaces', activeWorkspaceId || user?.uid || 'no-ws', 'cases', caseId)).then(snap => {
        if (snap.exists()) {
          const title = formatCaseTitle(snap.data().title) || 'Case File';
          useWorkspaceTabs.getState().updateTabTitle(path, title);
        } else {
          useWorkspaceTabs.getState().updateTabTitle(path, 'Case File');
        }
      }).catch(() => {
        useWorkspaceTabs.getState().updateTabTitle(path, 'Case File');
      });
    } else if (isDrafts) {
      const parts = path.split('/');
      let id = parts[2];
      let title = 'Loading...';
      
      if (id === 'system') {
        id = decodeURIComponent(parts[3]);
        if (!useWorkspaceTabs.getState().tabs.some((t) => t.id === path)) {
          addTab({ id: path, path, title: 'Loading...', type: 'draft', entityId: id });
        }
        fetchSystemTemplateFresh(id).then((tpl) => {
          const label = tpl ? getDisplayTemplateTitle(tpl) : id;
          useWorkspaceTabs.getState().updateTabTitle(path, label);
        }).catch(() => {
          useWorkspaceTabs.getState().updateTabTitle(path, id);
        });
      } else if (!useWorkspaceTabs.getState().tabs.some((t) => t.id === path)) {
        addTab({ id: path, path, title, type: 'draft', entityId: id });
        const wsId = activeWorkspaceId || user?.uid || 'no-ws';
        getDoc(doc(db, 'workspaces', wsId, 'documents', id)).then(async (snap) => {
          if (!snap.exists()) {
            useWorkspaceTabs.getState().updateTabTitle(path, 'Draft');
            return;
          }
          const data = snap.data();
          let caseTitle: string | undefined;
          if (data.caseId) {
            try {
              const caseSnap = await getDoc(
                doc(db, 'workspaces', wsId, 'cases', data.caseId as string),
              );
              if (caseSnap.exists()) {
                caseTitle = caseSnap.data().title as string;
              }
            } catch {
              /* keep document-only title */
            }
          }
          useWorkspaceTabs.getState().updateTabTitle(
            path,
            formatWorkspaceDraftTabTitle(data.title as string, caseTitle),
          );
        }).catch(() => {
          useWorkspaceTabs.getState().updateTabTitle(path, 'Draft');
        });
      }
    } else if (isNew) {
      addTab({ id: path, path, title: 'New Tab', type: 'new' });
    } else {
      // It's not a tabbable route, do we clear activeTab? No, keep it in background!
      // But wait! If user navigated directly via Sidebar to Dashboard, should we update activeTab?
      // State is preserved! But we need to visually unselect the tab if we are on a non-tab route?
      // Actually, we don't need to clear activeTab. The Outlet will just overlay it.
    }
  }, [location.pathname, activeWorkspaceId, userId, addTab, removeTab, setActiveTab]);

  useEffect(() => {
    if (!user?.uid) {
      setPlatformNotifications([]);
      return;
    }

    let cancelled = false;

    void (async () => {
      const rows = await fetchUserNotifications(user.uid);
      if (!cancelled) setPlatformNotifications(rows);
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid || bannerNotification) return;
    const next = pickAutoShowBannerNotification(platformNotifications);
    if (next) setBannerNotification(next);
  }, [user?.uid, platformNotifications, bannerNotification]);

  const closeBanner = useCallback(async (notification: UserNotification) => {
    try {
      if (!notification.bannerDismissedAt) {
        await dismissNotificationBanner(notification.id);
      }
      if (!notification.read) {
        await markNotificationRead(notification.id);
      }
    } catch (error) {
      console.error('Error closing notification banner:', error);
    } finally {
      setBannerNotification(null);
    }
  }, []);

  const openBanner = useCallback((notification: UserNotification) => {
    setBannerNotification(notification);
  }, []);
  useEffect(() => {
    if (!user) return;
    const ws = activeWorkspaceId || user.uid;
    if (!ws) return;

    const storageKey = `lexgat:purge-trash:${ws}`;
    const lastRun = Number(localStorage.getItem(storageKey) || 0);
    const oneHour = 60 * 60 * 1000;
    if (Date.now() - lastRun < oneHour) return;

    localStorage.setItem(storageKey, String(Date.now()));
    void purgeExpiredTrash(ws).catch((err) => {
      console.error('Auto-purge recycle bin failed:', err);
    });
  }, [user, activeWorkspaceId]);

  const isTabbableRoute =
    (location.pathname.startsWith('/cases/') && location.pathname !== '/cases') ||
    (location.pathname.startsWith('/drafts/') && location.pathname !== '/drafts') ||
    location.pathname === '/workspace/new';

  const activeTab = tabs.find((t) => t.id === activeTabId) ?? null;

  const { appearance, setAppearance, isGlass } = useAppearance();
  const { config } = useSystem();
  const isLexCounselOpen = useLexCounselStore((s) => s.isOpen);

  const closeContextMenu = useCallback(() => setContextMenu(null), []);

  const handleNewDraftForCase = useCallback(
    async (caseId: string) => {
      if (!user?.uid || !activeWorkspaceId) return;

      const docGate = await check('document');
      if (!docGate.allowed) {
        showError(docGate.reason ?? 'Document limit reached on your plan.');
        return;
      }

      try {
        await startBlankCaseDraftAndOpenEditor({
          caseId,
          userId: user.uid,
          workspaceId: activeWorkspaceId,
          caseTitle:
            activeTab?.type === 'case' && activeTab.entityId === caseId
              ? activeTab.title
              : undefined,
          navigate,
        });
        await recordUsage('documentsCreated');
      } catch (error) {
        console.error('Failed to create blank draft for case', error);
        handleFirestoreError(error, OperationType.CREATE, 'documents');
        showError('Could not create draft.');
      }
    },
    [user?.uid, activeWorkspaceId, activeTab, check, recordUsage, navigate, showError],
  );

  const handleEditTemplate = useCallback(
    (target: NonNullable<ReturnType<typeof getTemplateContextFromTarget>>) => {
      const navState = target.caseId
        ? {
            fromCaseId: target.caseId,
            draftsSubTab: target.draftsSubTab ?? ('recommended' as const),
          }
        : { fromDraftsScreen: true, activeTab: 'templates' as const };

      if (target.isSystem) {
        navigate(lexGatSystemTemplateEditPath(target.templateId), { state: navState });
        return;
      }
      navigate(`/drafts/${target.templateId}`, { state: navState });
    },
    [navigate],
  );

  const contextMenuItems = useMemo(
    () =>
      buildAppContextMenu({
        activeTab,
        isTabbableRoute,
        appearance,
        isMac: typeof navigator !== 'undefined' && /Mac|iPhone|iPod|iPad/i.test(navigator.platform),
        isAdmin,
        templateContext: contextMenu?.templateContext,
        onNewCase: () => {
          if (location.pathname === '/cases') {
            window.dispatchEvent(new CustomEvent('lexgat:open-new-case'));
          } else {
            navigate('/cases', { state: { openNewCase: true } });
          }
        },
        onNewDraft: () => {
          if (location.pathname === '/drafts') {
            window.dispatchEvent(new CustomEvent('lexgat:open-new-draft'));
          } else {
            navigate('/drafts', { state: { openNewDraft: true } });
          }
        },
        onNewDraftForCase: (caseId) => {
          void handleNewDraftForCase(caseId);
        },
        onEditTemplate: handleEditTemplate,
        onImportCase: () => navigate('/uploads?tab=old-cases'),
        onSearch: openWorkspaceSearch,
        onExportDocx: () => window.dispatchEvent(new Event('trigger-download-docx')),
        onNavigate: (path) => navigate(path),
        onSetAppearance: setAppearance,
        onSettings: () => navigate('/settings'),
      }),
    [
      activeTab,
      isTabbableRoute,
      appearance,
      contextMenu?.templateContext,
      isAdmin,
      location.pathname,
      navigate,
      openWorkspaceSearch,
      setAppearance,
      handleNewDraftForCase,
      handleEditTemplate,
    ],
  );

  useEffect(() => {
    const handleContextMenu = (e: MouseEvent) => {
      if (!shouldShowGlobalContextMenu(e)) return;
      e.preventDefault();
      setContextMenu({
        open: true,
        x: e.clientX,
        y: e.clientY,
        templateContext: getTemplateContextFromTarget(e.target) ?? undefined,
      });
    };
    document.addEventListener('contextmenu', handleContextMenu, true);
    return () => document.removeEventListener('contextmenu', handleContextMenu, true);
  }, []);

  const handleBannerOpenLink = useCallback(
    async (href: string) => {
      if (!bannerNotification) return;
      try {
        if (!bannerNotification.bannerDismissedAt) {
          await dismissNotificationBanner(bannerNotification.id);
        }
        if (!bannerNotification.read) {
          await markNotificationRead(bannerNotification.id);
        }
      } catch (error) {
        console.error('Error updating notification before navigation:', error);
      } finally {
        setBannerNotification(null);
        navigate(href);
      }
    },
    [bannerNotification, navigate],
  );

  // Library detail manages its own internal scroll (app-shell pattern), so
  // the outer Outlet wrapper must NOT scroll for /library/:id, otherwise the
  // page header would scroll away with the content.
  const isLibraryDetail = location.pathname.startsWith('/library/') && location.pathname !== '/library';

  return (
    <div className="flex h-screen w-full overflow-hidden sm:h-[100dvh]">
      <div
        className={`relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden pb-20 text-text-main md:pb-0 ${
          isGlass ? 'app-chrome' : 'bg-bg-app'
        }`}
      >
      <AppMainNav onPlatformNotificationClick={openBanner} />

      <main
        className="relative flex min-h-0 w-full flex-1 flex-col pt-0"
      >
        <WorkspaceTabsBar />
        <ProfileCompleteBanner />
        <PlatformAnnouncementBanner />
        <InAppNotificationBanner
          notification={bannerNotification}
          onDismiss={() => {
            if (bannerNotification) void closeBanner(bannerNotification);
          }}
          onOpenLink={handleBannerOpenLink}
        />
        {/* Main content: sidebar nav uses outlet; workspace tabs replace it (Gemini-style editor is tab-only) */}
        <div className="relative flex min-h-0 w-full flex-1 flex-col overflow-hidden">
          {!isTabbableRoute ? (
            <div
              data-app-scroll-region
              className={`min-h-0 w-full flex-1 ${
                isLibraryDetail ? "overflow-hidden" : "overflow-y-auto overflow-x-hidden"
              }`}
            >
              <RouteErrorBoundary routeName={location.pathname}>
                <AnimatedOutlet />
              </RouteErrorBoundary>
            </div>
          ) : !tabs.some((t) => t.id === location.pathname) ? (
            <div className="flex min-h-0 w-full flex-1 flex-col items-center justify-center text-sm text-slate-500">
              Loading workspace…
            </div>
          ) : (
            tabs.map((tab) => {
              const isActiveTab = location.pathname === tab.id;

              let content: ReactNode = null;
              if (tab.type === "case") {
                content = (
                  <CaseDetail providedCaseId={tab.entityId} isActiveTab={isActiveTab} />
                );
              } else if (tab.type === "draft" || tab.type === "document") {
                const isSystem = tab.path.includes("/system/");
                content = (
                  <DraftEditor
                    providedId={isSystem ? undefined : tab.entityId}
                    providedType={isSystem ? tab.entityId : undefined}
                    isActiveTab={isActiveTab}
                  />
                );
              } else if (tab.type === "new") {
                content = <NewTabSearch isActiveTab={isActiveTab} />;
              }

              return (
                <div
                  key={tab.id}
                  className={`relative flex min-h-0 w-full flex-1 flex-col ${
                    isActiveTab ? "" : "hidden"
                  } ${
                    isGlass && tab.type !== "draft" && tab.type !== "document"
                      ? "app-glass-surface"
                      : ""
                  } ${
                    tab.type !== "draft" && tab.type !== "document"
                      ? "overflow-y-auto overflow-x-hidden"
                      : "overflow-hidden"
                  }`}
                  aria-hidden={!isActiveTab}
                >
                  <Suspense fallback={isActiveTab ? <TabContentFallback /> : null}>
                    {content ?? (
                      <div className="flex flex-1 items-center justify-center text-sm text-slate-500">
                        Unable to open this tab.
                      </div>
                    )}
                  </Suspense>
                </div>
              );
            })
          )}
        </div>
      </main>

      <AppContextMenu
        state={contextMenu}
        items={contextMenuItems}
        onClose={closeContextMenu}
      />

      <LexCounselGlobalHost />

      </div>

      {config.enableAIFeatures ? (
        <AnimatePresence>{isLexCounselOpen ? <LexCounselPanel /> : null}</AnimatePresence>
      ) : null}
    </div>
  );
}
